/**
 * Features Slice
 *
 * Redux state management for feature access control.
 * Features are fetched once on login and cached until logout or refresh.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { featuresAPI, UserFeatureAccess, FeatureAccessResult } from '../services/featuresAPI';
import { isSessionExpired, SESSION_EXPIRED_SENTINEL } from '../services/api';
import { RootState } from './store';

/**
 * Features state interface
 */
interface FeaturesState {
  // User's feature access data
  featureAccess: UserFeatureAccess | null;
  // Loading state
  loading: boolean;
  // Error message
  error: string | null;
  // When features were last fetched
  lastFetched: number | null;
}

const initialState: FeaturesState = {
  featureAccess: null,
  loading: false,
  error: null,
  lastFetched: null,
};

/**
 * Fetch user's accessible features
 * Called on login and can be forced to refresh
 */
export const fetchFeatureAccess = createAsyncThunk(
  'features/fetchFeatureAccess',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;

      // Return cached data if available and not forcing refresh
      if (!forceRefresh && state.features.featureAccess && state.features.lastFetched) {
        return {
          data: state.features.featureAccess,
          fromCache: true,
        };
      }

      // Fetch fresh data from API
      const data = await featuresAPI.getAccessibleFeatures();
      return {
        data,
        fromCache: false,
      };
    } catch (error: any) {
      if (isSessionExpired(error)) return rejectWithValue(SESSION_EXPIRED_SENTINEL);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch features');
    }
  },
  {
    condition: (forceRefresh: boolean = false, { getState }) => {
      if (forceRefresh) return true;

      const state = getState() as RootState;

      // Coalesce concurrent hook initializers: once the first request is pending,
      // all other non-forced calls should reuse that in-flight load.
      if (state.features.loading) return false;

      // The feature access response is cached in Redux until logout or an explicit refresh.
      if (state.features.featureAccess && state.features.lastFetched) return false;

      return true;
    },
  }
);

/**
 * Check access to a specific feature
 */
export const checkFeatureAccess = createAsyncThunk(
  'features/checkFeatureAccess',
  async (featureId: string, { rejectWithValue }) => {
    try {
      const result = await featuresAPI.checkFeatureAccess(featureId);
      return { featureId, result };
    } catch (error: any) {
      if (isSessionExpired(error)) return rejectWithValue(SESSION_EXPIRED_SENTINEL);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to check feature access');
    }
  }
);

const featuresSlice = createSlice({
  name: 'features',
  initialState,
  reducers: {
    /**
     * Clear feature access data (on logout)
     */
    clearFeatureAccess: (state) => {
      state.featureAccess = null;
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
    },

    /**
     * Update a single feature's access (for optimistic updates)
     */
    updateFeatureAccess: (
      state,
      action: PayloadAction<{ featureId: string; access: FeatureAccessResult }>
    ) => {
      if (state.featureAccess) {
        state.featureAccess.features[action.payload.featureId] = action.payload.access;
      }
    },

    /**
     * Set error state
     */
    setFeaturesError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    // fetchFeatureAccess
    builder
      .addCase(fetchFeatureAccess.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureAccess.fulfilled, (state, action) => {
        state.loading = false;
        state.featureAccess = action.payload.data;
        if (!action.payload.fromCache) {
          state.lastFetched = Date.now();
        }
        state.error = null;
      })
      .addCase(fetchFeatureAccess.rejected, (state, action) => {
        state.loading = false;
        if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string;
      });

    // checkFeatureAccess
    builder
      .addCase(checkFeatureAccess.fulfilled, (state, action) => {
        // Update the feature access result in state
        if (state.featureAccess) {
          state.featureAccess.features[action.payload.featureId] = action.payload.result;
        }
      });
  },
});

// Export actions
export const { clearFeatureAccess, updateFeatureAccess, setFeaturesError } = featuresSlice.actions;

// Export reducer
export default featuresSlice.reducer;

// ============================================
// Selectors
// ============================================

/**
 * Select the full feature access state
 */
export const selectFeatureAccess = (state: RootState) => state.features.featureAccess;

/**
 * Select loading state
 */
export const selectFeaturesLoading = (state: RootState) => state.features.loading;

/**
 * Select error state
 */
export const selectFeaturesError = (state: RootState) => state.features.error;

/**
 * Select if user has access to a specific feature
 */
export const selectHasFeatureAccess = (featureId: string) => (state: RootState): boolean => {
  const featureAccess = state.features.featureAccess;
  if (!featureAccess) return false;

  const feature = featureAccess.features[featureId];
  return feature?.hasAccess ?? false;
};

/**
 * Select feature access result for a specific feature
 */
export const selectFeatureAccessResult = (featureId: string) => (state: RootState): FeatureAccessResult | null => {
  const featureAccess = state.features.featureAccess;
  if (!featureAccess) return null;

  return featureAccess.features[featureId] || null;
};

/**
 * Select all accessible feature IDs
 */
export const selectAccessibleFeatureIds = (state: RootState): string[] => {
  const featureAccess = state.features.featureAccess;
  if (!featureAccess) return [];

  return Object.entries(featureAccess.features)
    .filter(([_, access]) => access.hasAccess)
    .map(([featureId]) => featureId);
};

/**
 * Select all locked feature IDs (with upgrade info)
 */
export const selectLockedFeatures = (state: RootState): Array<{
  featureId: string;
  upgradeRequired?: string;
}> => {
  const featureAccess = state.features.featureAccess;
  if (!featureAccess) return [];

  return Object.entries(featureAccess.features)
    .filter(([_, access]) => !access.hasAccess)
    .map(([featureId, access]) => ({
      featureId,
      upgradeRequired: access.upgradeRequired,
    }));
};

/**
 * Select user's current tier from feature access
 */
export const selectUserTierFromFeatures = (state: RootState): string | null => {
  return state.features.featureAccess?.tier || null;
};

/**
 * Select whether launch promo is currently active
 */
export const selectIsLaunchPromo = (state: RootState): boolean => {
  return state.features.featureAccess?.isLaunchPromo ?? false;
};
